import head from 'lodash/head';
import { createSelector } from '@reduxjs/toolkit';
import { G_CORE_CLASS, PRODUCT_SERVICE_CODES, CATEGORY_SUFFIX } from '@yme/common/selectors';
import { BillingOptions, LOCAL_STORAGE_BILLING_OPTION_KEY, } from '@yme/components/CheckoutForm/ShippingDetailsForm/BillingAddress/constants';
import { getState } from '@yme/selectors/common';
import { getAttributeByName, getStreetWithAdditionalAddress } from '@yme/utils/common';
import { ACCESSORIES_ATTRIBUTE, ProductType } from '../../constants';
import { CartState, FINANCING_ENABLED, HOSTETTLER_ERROR_STATES, LEASING_ENABLED, PRODUCT_DELIVER_AT_DEALER_ONLY, } from './constants';
export const getCart = createSelector(getState, (state) => state.cart);
const getExternalCart = createSelector(getState, (state) => state.externalCart);
export const getCartId = createSelector(getCart, (state) => state?.data?.id);
export const getIsOpenModal = createSelector(getCart, (cart) => cart.isOpenModal);
export const getIsOpenModalExternal = createSelector(getExternalCart, (externalCart) => {
    return externalCart.data?.errorCode === HOSTETTLER_ERROR_STATES.SUCCESS_STATUS
        ? externalCart.isOpenModal
        : false;
});
export const getIsLoadingExternalCart = createSelector(getExternalCart, (externalCart) => {
    return externalCart.isLoadingCart;
});
export const getExternalCartTotalItems = createSelector(getExternalCart, (externalCart) => {
    if (!externalCart?.data) {
        return null;
    }
    return externalCart.data.cartTotalItems;
});
export const getCartCounter = createSelector(getCart, (cart) => {
    if (!cart.data) {
        return null;
    }
    return cart.data.entries.reduce((acc, item) => acc + item.quantity, 0);
});
export const getCartErrorData = createSelector(getCart, (cart) => cart.errorData);
export const getCartEntries = createSelector(getCart, (cart) => cart.data?.entries ?? []);
export const getIsAddingProduct = createSelector(getCart, (cart) => cart.isAddingProduct);
export const getIsLoadingCart = createSelector(getCart, (cart) => cart.isLoadingCart);
export const getCartById = createSelector(getCart, (cart) => cart.hyperdriveEndpoints.getCartById);
export const getCartPrices = createSelector(getCart, (cart) => ({
    subtotalPrice: cart.data?.subtotalPrice,
    totalPrice: cart.data?.totalPrice,
    totalDiscount: cart.data?.totalDiscount,
    paymentFee: cart.paymentFee,
}));
export const getShippingInfo = createSelector(getCart, (cart) => cart.data?.shippingInfo);
export const getHasProductDeliverAtDealerOnly = createSelector(getCartEntries, (entries) => entries.some((entry) => entry.product.variants[0].attributes.find((attribute) => attribute.name === PRODUCT_DELIVER_AT_DEALER_ONLY && attribute.value)));
export const getIsFinanceEnabled = createSelector(getCartEntries, (entries) => {
    const units = entries.filter((entry) => entry.product.type === ProductType.UNIT);
    if (!units.length) {
        return false;
    }
    return units.every((entry) => {
        return head(entry.product.variants)?.attributes?.find((attribute) => {
            if (attribute.name === FINANCING_ENABLED) {
                return !!attribute.value;
            }
        });
    });
});
export const getIsLeasingEnabled = createSelector(getCartEntries, (entries) => {
    const units = entries.filter((entry) => entry.product.type === ProductType.UNIT);
    if (!units.length) {
        return false;
    }
    return units.every((entry) => {
        return head(entry.product.variants)?.attributes?.find((attribute) => {
            if (attribute.name === LEASING_ENABLED) {
                return attribute.value;
            }
        });
    });
});
export const getIsOnlyUnits = createSelector(getCartEntries, (entries) => entries.every((entry) => entry.productType === ProductType.UNIT));
export const getIsOneAndOnlyOneUnit = createSelector([getCartEntries, getIsOnlyUnits], (entries, isOnlyUnits) => isOnlyUnits && entries.length === 1);
export const getIsOneUnitAndOneItem = createSelector([getIsOneAndOnlyOneUnit, getCartCounter], (isOneAndOnlyOneUnit, cartCounter) => isOneAndOnlyOneUnit && cartCounter === 1);
export const getAccessoriesAndApparels = createSelector(getCartEntries, (entries) => entries.filter((entry) => entry.product.type !== ProductType.UNIT));
export const getIsOnlyOneUnitWithApparelsAndAccessories = createSelector(getCartEntries, (entries) => {
    let unitCount = 0;
    let totalUnitQuantity = 0;
    for (const entry of entries) {
        if (entry.product.type === ProductType.UNIT) {
            unitCount += 1;
            totalUnitQuantity += entry.quantity;
        }
    }
    return unitCount === 1 && totalUnitQuantity === 1;
});
export const getIsAccessoriesAndApparels = createSelector(getAccessoriesAndApparels, (entries) => {
    const { APPAREL, ACCESSORY } = ProductType;
    return entries.every((entry) => [ACCESSORY, APPAREL].includes(entry.product.type));
});
export const getHasProductUnit = createSelector(getCartEntries, (entries) => entries.some((entry) => entry.productType === ProductType.UNIT));
export const getIsFilteredOnlyUnits = createSelector(getCartEntries, (entries) => entries.filter((entry) => entry.productType === ProductType.UNIT));
export const getHasProductApparel = createSelector(getCartEntries, (entries) => entries.some((entry) => entry.productType === ProductType.APPAREL));
export const getIsAccessoryRelatedToUnit = createSelector([getAccessoriesAndApparels, getIsFilteredOnlyUnits, getHasProductApparel], (accessoriesAndApparels, units, hasProductApparel) => {
    if (hasProductApparel || !units.length || (units.length && !accessoriesAndApparels.length)) {
        return false;
    }
    const keys = accessoriesAndApparels.map((unit) => unit.product.key);
    return keys.every((key) => units.some((unit) => unit.product.variants?.[0].attributes.find((attr) => {
        if (attr.name === ACCESSORIES_ATTRIBUTE) {
            return attr.value.includes(+key);
        }
    })));
});
export const getShippingMethodName = createSelector(getShippingInfo, (shippingInfo) => shippingInfo?.name);
export const getShippingMethods = createSelector(getCart, (cart) => cart.shippingMethods);
export const getDeletingProduct = createSelector(getCart, (cart) => cart.deletingProduct);
export const getCartLabels = createSelector(getCart, (cart) => cart.labels);
export const getValidationStockStatus = createSelector(getCart, (cart) => cart.validationStock.status);
export const getCartPersonalDetails = createSelector(getCart, (cart) => ({
    cartFirstName: cart.data?.billingAddress.firstName || '',
    cartLastName: cart.data?.billingAddress.lastName || '',
    cartPhone: cart.data?.billingAddress.phone || '',
    cartEmail: cart.data?.billingAddress.email || '',
}));
export const getReviewOrderAddressData = createSelector(getCart, (cart) => ({
    shippingAddressInfo: {
        firstName: cart.data?.shippingAddress.firstName || '',
        lastName: cart.data?.shippingAddress.lastName || '',
        address: {
            streetName: getStreetWithAdditionalAddress(cart.data?.shippingAddress.streetName, cart.data?.shippingAddress.additionalStreetInfo),
            city: cart.data?.shippingAddress.city || '',
            postalCode: cart.data?.shippingAddress.postalCode || '',
            state: cart.data?.shippingAddress.state || '',
            country: cart.data?.shippingAddress.country || '',
        },
        email: cart.data?.shippingAddress.email || '',
        phone: cart.data?.shippingAddress.phone || '',
    },
    billingAddressInfo: {
        firstName: cart.data?.billingAddress.firstName || '',
        lastName: cart.data?.billingAddress.lastName || '',
        address: {
            streetName: getStreetWithAdditionalAddress(cart.data?.billingAddress.streetName, cart.data?.billingAddress.additionalStreetInfo),
            city: cart.data?.billingAddress.city || '',
            postalCode: cart.data?.billingAddress.postalCode || '',
            state: cart.data?.billingAddress.state || '',
            country: cart.data?.billingAddress.country || '',
        },
        email: cart.data?.billingAddress.email || '',
        phone: cart.data?.billingAddress.phone || '',
    },
}));
export const getShippingDetailsInfo = createSelector(getCart, (cart) => ({
    city: cart.data?.shippingAddress.city || '',
    postalCode: cart.data?.shippingAddress.postalCode || '',
    address: getStreetWithAdditionalAddress(cart.data?.shippingAddress.streetName, cart.data?.shippingAddress.additionalStreetInfo),
    state: cart.data?.shippingAddress.state || '',
    country: cart.data?.shippingAddress.country || '',
}));
const getShippingAddressCountry = createSelector(getCart, (cart) => cart?.data?.shippingAddress.country);
const getBillingAddressCountry = createSelector(getCart, (cart) => cart?.data?.billingAddress.country);
export const getIsBillingShippingEqual = createSelector([getShippingAddressCountry, getBillingAddressCountry], (shippingCountry, billingCountry) => {
    if (shippingCountry && billingCountry) {
        return shippingCountry === billingCountry;
    }
    return false;
});
export const getBillingAddressInfo = createSelector(getCart, (cart) => {
    const billingOption = localStorage.getItem(LOCAL_STORAGE_BILLING_OPTION_KEY) || '';
    if (billingOption === BillingOptions.SAME_AS_SHIPPING_ADDRESS) {
        return {
            city: '',
            address: '',
            postalCode: '',
            state: '',
            country: '',
        };
    }
    return {
        city: cart.data?.billingAddress.city || '',
        postalCode: cart.data?.billingAddress.postalCode || '',
        address: getStreetWithAdditionalAddress(cart.data?.billingAddress.streetName, cart.data?.billingAddress.additionalStreetInfo),
        state: cart.data?.billingAddress.state || '',
        country: cart.data?.billingAddress.country || '',
    };
});
export const getCartEndpoint = createSelector(getCart, (cart) => cart.hyperdriveEndpoints.getCartById);
export const getHasDiscountCode = createSelector(getCart, (cart) => Boolean(cart.data?.discountCodes.length));
export const getCartQrId = createSelector(getCart, (cart) => cart.cartQrId);
export const getIsCartConvertedToOrder = createSelector(getCart, (cart) => cart.data?.state === CartState.ORDERED);
export const getCartUnitServiceCodes = createSelector(getCartEntries, (entries) => {
    const cartServiceCodes = entries.map(({ productType, product }) => {
        if (productType === ProductType.UNIT) {
            const productServiceCodes = getAttributeByName(product.variants[0].attributes, PRODUCT_SERVICE_CODES);
            return productServiceCodes ? productServiceCodes : '';
        }
        return '';
    });
    return Array.from(new Set(cartServiceCodes.filter((serviceCode) => serviceCode).flat()));
});
export const getCartServiceCodes = createSelector(getCartEntries, (entries) => {
    const cartServiceCodes = entries.map(({ productType, product }) => {
        if (productType === ProductType.UNIT) {
            const productServiceCodes = getAttributeByName(product.variants[0].attributes, PRODUCT_SERVICE_CODES);
            return productServiceCodes ? productServiceCodes : '';
        }
        if (productType === ProductType.ACCESSORY) {
            const lmsValue = getAttributeByName(product.variants[0].attributes, G_CORE_CLASS);
            return lmsValue ? `${lmsValue.toLowerCase()}${CATEGORY_SUFFIX}` : '';
        }
        return '';
    });
    return Array.from(new Set(cartServiceCodes.filter((serviceCode) => serviceCode).flat()));
});
export const getIsAllProductsApparel = createSelector(getCartEntries, (entries) => entries.every((entry) => entry.productType === ProductType.APPAREL));
export const getAllProductsApparel = createSelector(getCartEntries, (entries) => entries.filter((product) => product.productType === ProductType.APPAREL));
